import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, ArrowRight, ArrowLeft, ChevronDown } from "lucide-react";

import { toast } from '../../utils/toast.js';
const networkPresets = ["UN Network","European Partnerships","Eastern Partnership Civil Society","Cooperation with Diaspora","International Financial Network"];

const countryCodes = [
  { code: "+374", country: "Armenia", flag: "🇦🇲" },
  { code: "+1", country: "USA/Canada", flag: "🇺🇸" },
  { code: "+44", country: "UK", flag: "🇬🇧" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+7", country: "Russia/Kazakhstan", flag: "🇷🇺" },
  { code: "+995", country: "Georgia", flag: "🇬🇪" },
  { code: "+98", country: "Iran", flag: "🇮🇷" },
  { code: "+972", country: "Israel", flag: "🇮🇱" },
  { code: "+971", country: "UAE", flag: "🇦🇪" },
  { code: "+375", country: "Belarus", flag: "🇧🇾" },
  { code: "+380", country: "Ukraine", flag: "🇺🇦" },
  { code: "+40", country: "Romania", flag: "🇷🇴" }
];

// Persisted on every keystroke so a reload or a back/forward navigation while still on
// this step doesn't lose what's been typed — see the matching load/save effects below.
const DRAFT_KEY = "orgOnboardingDraftStepTwo";
const defaultFormData = {
  orgContactEmail: "",
  orgContactPhone: { code: "+374", number: "" },
  address: "", website: "",
  socialLinks: [""],
  networks: [], customNetwork: ""
};

function Regnext() {
  const { t } = useTranslation();
  const [stepOneSnapshot, setStepOneSnapshot] = useState(null);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(DRAFT_KEY);
      return saved ? { ...defaultFormData, ...JSON.parse(saved) } : defaultFormData;
    } catch {
      return defaultFormData;
    }
  });
  const [showPhoneCodeDropdown, setShowPhoneCodeDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth <= 768 : false);

  const navigate = useNavigate();

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    const stored = sessionStorage.getItem("orgOnboardingStepOne");
    if (stored) setStepOneSnapshot(JSON.parse(stored));
    else navigate("/reg");
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".phone-code-dropdown")) setShowPhoneCodeDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateFormData = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
  const handlePhoneNumberChange = (value) => setFormData(prev => ({ ...prev, orgContactPhone: { ...prev.orgContactPhone, number: value.replace(/\D/g, "") } }));
  const selectPhoneCode = (code) => { setFormData(prev => ({ ...prev, orgContactPhone: { ...prev.orgContactPhone, code } })); setShowPhoneCodeDropdown(false); };

  const handleSocialLinkChange = (value, index) => setFormData(prev => { const next = [...prev.socialLinks]; next[index] = value; return { ...prev, socialLinks: next }; });
  const addSocialLink = () => setFormData(prev => ({ ...prev, socialLinks: [...prev.socialLinks, ""] }));
  const removeSocialLink = (index) => setFormData(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, i) => i !== index) }));
  const toggleNetwork = (n) => setFormData(prev => ({ ...prev, networks: prev.networks.includes(n) ? prev.networks.filter(x => x !== n) : [...prev.networks, n] }));
  const addCustomNetwork = () => { const t = formData.customNetwork.trim(); if (t && !formData.networks.includes(t)) updateFormData("networks", [...formData.networks, t]); updateFormData("customNetwork", ""); };
  const removeNetwork = (n) => setFormData(prev => ({ ...prev, networks: prev.networks.filter(x => x !== n) }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const email = formData.orgContactEmail.trim();
    if (!email || !formData.orgContactPhone.number) { toast(t("authPages.orgReg.alertRequiredFields")); return; }
    if (!email.includes("@") || !email.includes(".")) { toast(t("authPages.orgReg.alertValidEmail")); return; }
    if (formData.orgContactPhone.number.length < 6) { toast(t("authPages.orgReg.alertValidPhone")); return; }
    if (!formData.address.trim()) { toast(t("authPages.orgReg.alertAddress")); return; }
    sessionStorage.setItem("orgOnboardingStepTwo", JSON.stringify({ ...formData, orgContactEmail: email, orgContactPhone: `${formData.orgContactPhone.code}${formData.orgContactPhone.number}` }));
    navigate("/reeg");
  };

  // Shared styles
  const inputStyle = { width: "100%", padding: isMobile ? "10px 12px" : "11px 14px", borderRadius: "10px", border: "1.5px solid #ddd", fontSize: isMobile ? "13px" : "14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s", background: "white" };
  const labelStyle = { display: "flex", flexDirection: "column", gap: "5px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" };
  const sectionStyle = { background: "white", borderRadius: "14px", padding: isMobile ? "14px 12px" : "20px", border: "1px solid #e9ecef", display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" };
  const sectionTitleStyle = { fontSize: isMobile ? "13px" : "15px", fontWeight: 700, color: "#1a3d2e", margin: 0 };
  const dropdownMenuStyle = { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "1.5px solid #ddd", borderRadius: "10px", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 200, maxHeight: "200px", overflowY: "auto" };
  const ghostBtnStyle = { background: "none", border: "1.5px dashed #32614d", borderRadius: "8px", color: "#32614d", padding: isMobile ? "8px 12px" : "9px 16px", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", fontFamily: "inherit" };

  if (!stepOneSnapshot) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
      <p style={{ color: "#666" }}>{t("authPages.orgReg.loadingOrgData")}</p>
    </div>
  );

  return (
    <div style={{ background: "#f5f7f0", minHeight: "100vh" }}>
      {/* Progress header */}
      <div style={{ background: "white", borderBottom: "1px solid #e9ecef", padding: isMobile ? "12px 14px" : "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: isMobile ? "6px" : "0" }}>
          {[
            { n: 1, label: isMobile ? t("authPages.orgReg.stepProfileShort") : t("authPages.orgReg.stepProfileLong"), done: true },
            { n: 2, label: isMobile ? t("authPages.orgReg.stepContactShort") : t("authPages.orgReg.stepContactLong"), active: true },
            { n: 3, label: isMobile ? t("authPages.orgReg.stepTeamShort") : t("authPages.orgReg.stepTeamLong") },
            { n: 4, label: t("authPages.orgReg.stepVerification") },
          ].map((step, i) => (
            <React.Fragment key={step.n}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", flex: 1 }}>
                <div style={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, borderRadius: "50%", background: step.done ? "#c8e6c9" : step.active ? "#32614d" : "#e9ecef", color: step.done ? "#2e7d32" : step.active ? "white" : "#999", display: "flex", alignItems: "center", justifyContent: "center", fontSize: isMobile ? "12px" : "13px", fontWeight: 700 }}>
                  {step.done ? "✓" : step.n}
                </div>
                <span style={{ fontSize: isMobile ? "9px" : "11px", color: step.active ? "#32614d" : step.done ? "#2e7d32" : "#999", fontWeight: step.active ? 700 : 400, textAlign: "center", whiteSpace: "nowrap" }}>{step.label}</span>
              </div>
              {i < 3 && <div style={{ height: "2px", flex: 2, background: "#e9ecef", marginBottom: isMobile ? "16px" : "20px" }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: isMobile ? "16px 14px 100px" : "28px 20px 60px" }}>

        {/* Heading + back */}
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", justifyContent: "space-between", marginBottom: "16px", flexDirection: isMobile ? "column" : "row", gap: "8px" }}>
          <div>
            <h2 style={{ fontSize: isMobile ? "15px" : "20px", fontWeight: 700, color: "#1a3d2e", margin: "0 0 4px" }}>{t("authPages.orgReg.step2Title")}</h2>
            <p style={{ fontSize: isMobile ? "11px" : "13px", color: "#888", margin: 0 }}>{t("authPages.orgReg.step2Subtitle")}</p>
          </div>
          <Link to="/reg" style={{ display: "flex", alignItems: "center", gap: "6px", color: "#32614d", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, textDecoration: "none", border: "1.5px solid #32614d", borderRadius: "8px", padding: "6px 12px", whiteSpace: "nowrap" }}>
            <ArrowLeft size={14} /> {t("authPages.orgReg.back")}
          </Link>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "16px" }}>

          {/* Contact Info */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.contactInfoTitle")}</h3>

            {/* Email */}
            <label style={labelStyle}>
              <span>{t("authPages.orgReg.orgEmail")} <span style={{ color: "#e53935" }}>*</span></span>
              <input type="email" value={formData.orgContactEmail} onChange={e => updateFormData("orgContactEmail", e.target.value)}
                placeholder={t("authPages.orgReg.orgEmailPlaceholder")} required style={inputStyle}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              <span style={{ fontSize: "11px", color: "#888" }}>{t("authPages.orgReg.orgEmailHint")}</span>
            </label>

            {/* Phone */}
            <label style={labelStyle}>
              <span>{t("authPages.orgReg.primaryPhone")} <span style={{ color: "#e53935" }}>*</span></span>
              <div style={{ display: "flex", gap: "6px" }}>
                <div className="phone-code-dropdown" style={{ position: "relative", width: isMobile ? "90px" : "110px", flexShrink: 0 }}>
                  <div onClick={() => setShowPhoneCodeDropdown(p => !p)}
                    style={{ ...inputStyle, height: isMobile ? "38px" : "42px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "4px" }}>
                    <span style={{ fontSize: isMobile ? "12px" : "13px" }}>{formData.orgContactPhone.code}</span>
                    <ChevronDown size={13} />
                  </div>
                  {showPhoneCodeDropdown && (
                    <div style={{ ...dropdownMenuStyle, width: "200px" }}>
                      {countryCodes.map(c => (
                        <div key={c.code} onClick={() => selectPhoneCode(c.code)}
                          style={{ padding: isMobile ? "8px 10px" : "9px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", borderBottom: "1px solid #f5f5f5", fontSize: isMobile ? "12px" : "13px" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f0faf4"}
                          onMouseLeave={e => e.currentTarget.style.background = "white"}>
                          <span>{c.flag}</span>
                          <span style={{ fontWeight: 600 }}>{c.code}</span>
                          <span style={{ color: "#888", fontSize: "11px" }}>{c.country}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input type="tel" value={formData.orgContactPhone.number} onChange={e => handlePhoneNumberChange(e.target.value)}
                  required placeholder={t("authPages.orgReg.phoneNumberPlaceholder")} style={{ ...inputStyle, height: isMobile ? "38px" : "42px", flex: 1 }}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </div>
              {formData.orgContactPhone.number && <span style={{ fontSize: "11px", color: "#32614d" }}>✓ {formData.orgContactPhone.code}{formData.orgContactPhone.number}</span>}
            </label>

            {/* Website + Address */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "10px" }}>
              <label style={labelStyle}>
                {t("authPages.orgReg.website")}
                <input type="url" value={formData.website} onChange={e => updateFormData("website", e.target.value)}
                  placeholder="https://example.com" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>
              <label style={labelStyle}>
                <span>{t("authPages.orgReg.address")} <span style={{ color: "#e53935" }}>*</span></span>
                <input type="text" value={formData.address} onChange={e => updateFormData("address", e.target.value)}
                  placeholder={t("authPages.orgReg.addressPlaceholder")} required style={inputStyle}
                  onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
              </label>
            </div>

            {/* Social Links */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <span style={{ fontSize: isMobile ? "12px" : "13px", fontWeight: 600, color: "#444" }}>{t("authPages.orgReg.socialLinks")}</span>
              {formData.socialLinks.map((link, i) => (
                <div key={i} style={{ display: "flex", gap: "8px" }}>
                  <input type="url" placeholder={t("authPages.orgReg.socialLinkPlaceholder")} value={link}
                    onChange={e => handleSocialLinkChange(e.target.value, i)}
                    style={{ ...inputStyle, flex: 1 }}
                    onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"} />
                  {formData.socialLinks.length > 1 && (
                    <button type="button" onClick={() => removeSocialLink(i)}
                      style={{ padding: "0 10px", background: "#ffebee", border: "none", borderRadius: "8px", cursor: "pointer", color: "#e53935", display: "flex", alignItems: "center" }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" style={ghostBtnStyle} onClick={addSocialLink}>
                <Plus size={14} /> {t("authPages.orgReg.addLink")}
              </button>
            </div>
          </section>

          {/* Networks */}
          <section style={sectionStyle}>
            <h3 style={sectionTitleStyle}>{t("authPages.orgReg.networksTitle")}</h3>
            <p style={{ margin: 0, fontSize: isMobile ? "11px" : "12px", color: "#888" }}>{t("authPages.orgReg.networksDesc")}</p>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {networkPresets.map(n => {
                const active = formData.networks.includes(n);
                return (
                  <label key={n}
                    style={{ padding: isMobile ? "7px 10px" : "8px 14px", borderRadius: "999px", border: `1.5px solid ${active ? "#32614d" : "#ddd"}`, background: active ? "#e8f5e9" : "white", cursor: "pointer", fontSize: isMobile ? "11px" : "12px", fontWeight: 600, color: active ? "#1a3d2e" : "#555", transition: "all 0.18s" }}>
                    {/* Toggle on the checkbox's onChange (not a label onClick) — clicking a label
                        wrapping its control also fires a synthetic click on the control itself, so
                        having both double-fires the toggle and cancels it back out. */}
                    <input type="checkbox" checked={active} onChange={() => toggleNetwork(n)} style={{ display: "none" }} />
                    {active && "✓ "}{n}
                  </label>
                );
              })}
            </div>

            {/* Custom network */}
            <div style={{ display: "flex", gap: "8px" }}>
              <input type="text" placeholder={t("authPages.orgReg.customNetworkPlaceholder")} value={formData.customNetwork}
                onChange={e => updateFormData("customNetwork", e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
                onFocus={e => e.target.style.borderColor = "#32614d"} onBlur={e => e.target.style.borderColor = "#ddd"}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCustomNetwork(); } }} />
              <button type="button" onClick={addCustomNetwork}
                style={{ padding: "0 16px", background: "#32614d", border: "none", borderRadius: "10px", color: "white", cursor: "pointer", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap" }}>
                {t("authPages.orgReg.add")}
              </button>
            </div>

            {formData.networks.some(n => !networkPresets.includes(n)) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {formData.networks.filter(n => !networkPresets.includes(n)).map(n => (
                  <span key={n} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#e8f5e9", color: "#1a3d2e", padding: "3px 8px 3px 10px", borderRadius: "999px", fontSize: isMobile ? "11px" : "12px", fontWeight: 600 }}>
                    {n}
                    <button type="button" onClick={() => removeNetwork(n)}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#1a3d2e", display: "flex", lineHeight: 1 }}>
                      <Trash2 size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Submit */}
          <button type="submit"
            style={{ padding: isMobile ? "13px 20px" : "14px 28px", background: "linear-gradient(135deg,#32614d,#2a5240)", color: "white", border: "none", borderRadius: "12px", fontSize: isMobile ? "14px" : "15px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontFamily: "inherit" }}>
            {t("authPages.orgReg.continueStep3")} <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default Regnext;